import { type FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSiteContent } from '@/context/ContentProvider';

interface SEOProps {
  title?: string;
  description?: string;
  path?: string;
  type?: string;
  image?: string;
  /** For blog articles: published date (ISO string) */
  publishedDate?: string;
  /** For blog articles: tags/keywords */
  keywords?: string[];
}

const BASE_URL = import.meta.env.VITE_SITE_URL || '';

export const SEO: FC<SEOProps> = ({
  title,
  description,
  path = '',
  type = 'website',
  image,
  publishedDate,
  keywords,
}) => {
  const siteContent = useSiteContent();
  const SITE_TITLE = siteContent.meta.siteTitle;
  const SITE_DESCRIPTION = siteContent.meta.siteDescription;
  const SITE_NAME = SITE_TITLE.split('—')[0]?.trim() || SITE_TITLE;

  const resolvedDescription = description || SITE_DESCRIPTION;
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_TITLE;
  const url = `${BASE_URL}${path}`;

  // Dynamic OG image — falls back to custom image if provided
  const ogImage = image
    ? `${BASE_URL}${image}`
    : `${BASE_URL}/api/og?title=${encodeURIComponent(title || SITE_NAME)}&subtitle=${encodeURIComponent(resolvedDescription.slice(0, 120))}&type=${type}&status=${encodeURIComponent(siteContent.hero.statusBadge || '')}`;

  const siteUrl = BASE_URL || '';

  // JSON-LD: WebSite schema — helps Google show sitelinks
  const websiteJsonLd = path === '' ? JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: siteUrl,
    description: SITE_DESCRIPTION,
    author: { '@type': 'Person', name: 'Kostiantyn Tarasenko' },
  }) : undefined;

  // JSON-LD: SiteNavigationElement — tells Google the main nav structure
  const navigationJsonLd = path === '' ? JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      { '@type': 'SiteNavigationElement', name: 'About', url: `${siteUrl}/about` },
      { '@type': 'SiteNavigationElement', name: 'Experience', url: `${siteUrl}/experience` },
      { '@type': 'SiteNavigationElement', name: 'Projects', url: `${siteUrl}/projects` },
      { '@type': 'SiteNavigationElement', name: 'Blog', url: `${siteUrl}/blog` },
      { '@type': 'SiteNavigationElement', name: 'Store', url: `${siteUrl}/store` },
      { '@type': 'SiteNavigationElement', name: 'Contact', url: `${siteUrl}/contact` },
    ],
  }) : undefined;

  // JSON-LD: Person schema for the main site
  const personJsonLd = type === 'website' ? JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: 'Kostiantyn Tarasenko',
    url: siteUrl,
    jobTitle: 'Senior Software Engineer',
    description: SITE_DESCRIPTION,
    sameAs: [
      'https://github.com/kostiantyn-tarasenko',
      'https://linkedin.com/in/kostiantyn-tarasenko',
    ],
    knowsAbout: ['React', 'TypeScript', 'JavaScript', 'Node.js', 'Web Development'],
  }) : undefined;

  // JSON-LD: Article schema for blog posts
  const articleJsonLd = type === 'article' ? JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: title,
    description: resolvedDescription,
    url,
    image: ogImage,
    datePublished: publishedDate,
    author: {
      '@type': 'Person',
      name: 'Kostiantyn Tarasenko',
      url: BASE_URL || '',
    },
    publisher: {
      '@type': 'Person',
      name: 'Kostiantyn Tarasenko',
    },
    ...(keywords?.length ? { keywords: keywords.join(', ') } : {}),
  }) : undefined;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={resolvedDescription} />
      <link rel="canonical" href={url} />
      {keywords?.length && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}

      {/* OpenGraph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={resolvedDescription} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      {publishedDate && (
        <meta property="article:published_time" content={publishedDate} />
      )}

      {/* RSS */}
      <link rel="alternate" type="application/rss+xml" title={`${SITE_NAME} — Blog`} href={`${BASE_URL}/rss.xml`} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={resolvedDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {websiteJsonLd && (
        <script type="application/ld+json">{websiteJsonLd}</script>
      )}
      {navigationJsonLd && (
        <script type="application/ld+json">{navigationJsonLd}</script>
      )}
      {personJsonLd && (
        <script type="application/ld+json">{personJsonLd}</script>
      )}
      {articleJsonLd && (
        <script type="application/ld+json">{articleJsonLd}</script>
      )}
    </Helmet>
  );
};
